/**
 * The words the banquet-wastage screens print, in one place.
 *
 * The Revert Banquet popup, the Expense modal's "Banquet · …" badge and its
 * single-wastage revert dialog all name the same things — an event, a day, a
 * rupee figure, who recorded an entry and when — and they must say them the
 * same way. Pure functions only: nothing here decides whether anything may be
 * reverted. That is always the server's verdict; these only phrase it.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const IST_OFFSET_MS = 330 * 60 * 1000;

/** "2026-09-27" (or an ISO timestamp starting with it) → "27 Sep". */
export function shortDate(value) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return value ? String(value) : '';
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
}

/** Indian digit grouping; paise only when the value carries them. */
export function rupees(n) {
  const value = Number(n) || 0;
  const hasPaise = Math.abs(value - Math.round(value)) > 0.004;
  return `₹${value.toLocaleString('en-IN', {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  })}`;
}

/** 5, "kg" → "5 kg". */
export function qtyLabel(quantity, unit) {
  const q = Number(quantity) || 0;
  const n = q.toLocaleString('en-IN', { maximumFractionDigits: 3 });
  return unit ? `${n} ${unit}` : n;
}

/**
 * When an entry was recorded, as the kitchen reads a clock: "27 Sep 10:15 pm".
 * A timestamp with a zone (the API's ISO strings) is shown in IST; one without
 * a zone is already local and is taken as written.
 */
export function recordedAtLabel(value) {
  if (!value) return '';
  let y; let mo; let d; let h; let mi;
  const s = value instanceof Date ? value.toISOString() : String(value);
  const zoned = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(s);
  if (zoned) {
    const t = new Date(s).getTime();
    if (!Number.isFinite(t)) return '';
    const ist = new Date(t + IST_OFFSET_MS);
    y = ist.getUTCFullYear(); mo = ist.getUTCMonth(); d = ist.getUTCDate();
    h = ist.getUTCHours(); mi = ist.getUTCMinutes();
  } else {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!m) return '';
    y = Number(m[1]); mo = Number(m[2]) - 1; d = Number(m[3]);
    h = Number(m[4]); mi = Number(m[5]);
  }
  if (!Number.isFinite(y) || mo < 0 || mo > 11) return '';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${d} ${MONTHS[mo]} ${h12}:${String(mi).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
}

/** "Sharma Reception · 27 Sep". */
export function banquetEventLabel(functionName, eventDate) {
  const name = functionName || 'Banquet';
  const day = shortDate(eventDate);
  return day ? `${name} · ${day}` : name;
}

/** The Expense modal's badge on a banquet wastage row. */
export function banquetBadgeLabel(row) {
  return row && row.functionName ? `Banquet · ${row.functionName}` : 'Banquet';
}

/**
 * The extra line in the single-wastage revert dialog for a banquet row: the
 * wastage came off THAT banquet's gross profit, so reverting it puts it back
 * there. An uncosted row moved no profit, so no amount is claimed for it.
 */
export function banquetRevertNote({ functionName, eventDate, amount } = {}) {
  const first = `This is banquet wastage for ${banquetEventLabel(functionName, eventDate)}.`;
  const value = Number(amount);
  return value > 0 ? `${first} Its gross profit will go up by ${rupees(value)}.` : first;
}

/** "Revert "Sharma Reception · 27 Sep"?" */
export function revertQuestion({ functionName, eventDate } = {}) {
  return `Revert "${banquetEventLabel(functionName, eventDate)}"?`;
}

/** "This will also revert 2 banquet wastage entries (₹2,250) recorded for this event:" */
export function wastageIntro(count, total) {
  const n = Number(count) || 0;
  return `This will also revert ${n} banquet wastage ${n === 1 ? 'entry' : 'entries'} (${rupees(total)}) recorded for this event:`;
}

/** "Paneer — 5 kg — ₹1,800 — recorded by Ravi, 27 Sep 10:15 pm" */
export function wastageEntryLine(w = {}) {
  const at = recordedAtLabel(w.recordedAt);
  let recorded = '';
  if (w.recordedBy && at) recorded = `recorded by ${w.recordedBy}, ${at}`;
  else if (w.recordedBy) recorded = `recorded by ${w.recordedBy}`;
  else if (at) recorded = `recorded ${at}`;
  return [w.itemName || 'Wastage entry', qtyLabel(w.quantity, w.unit), rupees(w.cost), recorded]
    .filter(Boolean)
    .join(' — ');
}

/** "Paneer — 5 kg (27 Sep)" — or as much of it as is known. */
export function lockedEntryLabel(w = {}) {
  const hasQty = w.quantity !== undefined && w.quantity !== null && w.quantity !== '';
  const base = hasQty ? `${w.itemName || 'Wastage entry'} — ${qtyLabel(w.quantity, w.unit)}` : (w.itemName || 'Wastage entry');
  const day = shortDate(w.wastageDay);
  return day ? `${base} (${day})` : base;
}

/**
 * Why one wastage entry holds the banquet back, in the server's own words. A
 * reason that is a clause ("is inside a period closed by …") continues the
 * sentence; a reason that is a sentence of its own follows a colon.
 */
export function lockedEntrySentence(w = {}) {
  const label = lockedEntryLabel(w);
  const reason = String(w.reason || '').trim() || 'can no longer be reverted.';
  return /^[a-z]/.test(reason)
    ? `The wastage entry ${label} ${reason}`
    : `The wastage entry ${label}: ${reason}`;
}

/** "Banquet reverted · stock restored for 14 ingredients · 2 wastage entries reverted." */
export function revertSuccessHeadline({ ingredients = 0, wastageCount = 0 } = {}) {
  const n = Number(ingredients) || 0;
  const m = Number(wastageCount) || 0;
  const parts = ['Banquet reverted'];
  if (n > 0) parts.push(`stock restored for ${n} ingredient${n === 1 ? '' : 's'}`);
  if (m > 0) parts.push(`${m} wastage ${m === 1 ? 'entry' : 'entries'} reverted`);
  return parts.join(' · ') + (m > 0 ? '.' : '');
}
